import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DataGridProps,
  GridColDef,
  GridRowId,
  GridSelectionModel,
} from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'

import DataGridToolbar from 'shared/components/DataGridToolbar'

const INITIAL_PAGE_SIZE = 10
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]

export interface SelectFilterVariables {
  first?: number | null
  after?: string | null
  name?: string | null
}

interface SelectFilterConnection {
  nodes?: Array<{ id: string; name: string }> | null
  totalCount?: number | null
  pageInfo?: { endCursor?: string | null } | null
}

interface UseDataGridSelectFilterOptions<TData> {
  document: TypedDocumentNode<TData, SelectFilterVariables>
  selectConnection: (data?: TData) => SelectFilterConnection | null | undefined
  columnLabel: string
  initialSelectedIds?: string[]
  onSelectedIdsChange?: (ids: string[]) => void
}

export const useDataGridSelectFilter = <TData>({
  document,
  selectConnection,
  columnLabel,
  initialSelectedIds = [],
  onSelectedIdsChange,
}: UseDataGridSelectFilterOptions<TData>) => {
  const { t } = useTranslation()
  const [selectionModel, setSelectionModel] =
    useState<GridSelectionModel>(initialSelectedIds)

  // options are fetched only after the filter is opened or something is already selected
  const hasInitialSelection = initialSelectedIds.length > 0
  const [isEnabled, setIsEnabled] = useState(hasInitialSelection)

  useEffect(() => {
    if (hasInitialSelection) setIsEnabled(true)
  }, [hasInitialSelection])

  const handleOpen = useCallback(() => setIsEnabled(true), [])

  const initialSelectedIdsKey = initialSelectedIds.join(',')
  useEffect(() => {
    if (selectionModel.map(String).join(',') !== initialSelectedIdsKey) {
      setSelectionModel(initialSelectedIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedIdsKey])

  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE)
  const mapPageToNextCursor = useRef<{ [page: number]: GridRowId }>({})
  const [page, setPage] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  const getVariables = (targetPage: number, query: string) => ({
    first: pageSize,
    after: mapPageToNextCursor.current[targetPage - 1]?.toString() || undefined,
    name: query || undefined,
  })

  const { data, loading, error, refetch } = useQuery(document, {
    skip: !isEnabled,
    variables: getVariables(page, searchQuery),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const connection = selectConnection(data)
  const endCursor = connection?.pageInfo?.endCursor
  const totalCount = connection?.totalCount ?? undefined
  const rows = useMemo(
    () => connection?.nodes?.map(({ id, name }) => ({ id, name })) || [],
    [connection]
  )

  useEffect(() => {
    if (!loading && endCursor) {
      mapPageToNextCursor.current[page] = endCursor
    }
  }, [page, loading, endCursor])

  const [rowCountState, setRowCountState] = useState(totalCount ?? 0)
  useEffect(() => {
    setRowCountState(prev => (totalCount !== undefined ? totalCount : prev))
  }, [totalCount])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: columnLabel,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
      },
    ],
    [columnLabel]
  )

  const handlePageChange = (newPage: number) => {
    if (newPage === 0 || mapPageToNextCursor.current[newPage - 1]) {
      setPage(newPage)
      refetch(getVariables(newPage, searchQuery))
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setPage(0)
    mapPageToNextCursor.current = {}
    refetch(getVariables(0, query))
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
    mapPageToNextCursor.current = {}
  }

  const handleSelectionModelChange = (newSelection: GridSelectionModel) => {
    setSelectionModel(newSelection)
    onSelectedIdsChange?.(newSelection.map(String))
  }

  const dataGridProps: DataGridProps = {
    columns,
    rows,
    loading,
    error,
    pagination: true,
    pageSize,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    rowCount: rowCountState,
    paginationMode: 'server',
    onPageChange: handlePageChange,
    page,
    checkboxSelection: true,
    onSelectionModelChange: handleSelectionModelChange,
    keepNonExistentRowsSelected: true,
    selectionModel,
    onPageSizeChange: handlePageSizeChange,
    localeText: {
      noRowsLabel: t('noResults'),
      noResultsOverlayLabel: t('noResults'),
    },
    components: { Toolbar: DataGridToolbar },
    componentsProps: {
      toolbar: {
        searchQuery,
        onSearch: handleSearch,
        isLoading: loading,
      },
    },
  }

  return {
    dataGridProps,
    selectedRowsCount: selectionModel.length,
    onOpen: handleOpen,
    retry: () => refetch(getVariables(page, searchQuery)),
  }
}
